import { useState } from 'react';
import { Marker as MapMarker } from 'react-map-gl';

import { css, cx } from '@emotion/css';
import { BoxProps } from '@opendoor/bricks/core/Box';
import { Flex, FlexProps, Icon, Text } from '@opendoor/bricks/core';
import { space } from '@opendoor/bricks/theme/eero';
import { IconProps } from '@opendoor/bricks/core/Icon';

const size = space[3] + 'px';
const ContainerStyles = css`
  content: '""';
  top: '95%';
  left: '50%';
  box-sizing: 'border-box';
  border: 'solid';
  position: 'absolute';
  border-width: ${size};
  margin-left: ${size};
  transform-origin: '0 0';
  box-shadow: '-3px 3px 3px rgb(29 71 123 / 4%)';
`;
export type MarkerIcon = 'open' | 'logo' | 'new-construction';

export interface MarkerProps {
  id: string;
  latitude: number;
  longitude: number;
  content?: string;
  selected?: boolean;
  visited?: boolean;
  index?: number;
  onClick?: (index: number | undefined) => void;
  onHover?: (e?: React.MouseEvent<HTMLDivElement> | null) => void;
  hoveredPropertyId?: string | null;
  iconName?: MarkerIcon;
}

const ICON_CONFIG: Record<MarkerIcon, IconProps> = {
  open: {
    name: 'open',
    size: 24,
    viewBox: undefined,
    mr: 2,
  },
  logo: {
    name: 'logo',
    size: 12,
    viewBox: '0 0 12 12',
    mr: 1,
  },
  'new-construction': {
    name: 'new-construction',
    size: 12,
    viewBox: '0 0 12 12',
    mr: 2,
  },
};

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/**
 * Marker component for use in the Map component.
 */
export const Marker: React.FC<MarkerProps> = ({
  id,
  selected,
  visited,
  content,
  index,
  latitude,
  longitude,
  onClick,
  onHover,
  hoveredPropertyId,
  iconName,
}) => {
  const [hovering, setHovering] = useState(false);
  const focus = selected || hovering;
  if (!longitude || !latitude) {
    return null;
  }

  const handleOnHover = (e: React.MouseEvent<HTMLDivElement>) => {
    if (onHover) {
      onHover(e);
    }
    setHovering(true);
  };

  const handleOnClick = () => {
    if (onClick) {
      onClick(index);
    }
  };

  const handleOnMouseOut = () => {
    if (onHover) {
      onHover(undefined);
    }
    setHovering(false);
  };

  const iconConfig = iconName && ICON_CONFIG[iconName];

  let styleProps: FlexProps;
  if (focus || hoveredPropertyId === id) {
    styleProps = { color: 'neutrals100', background: 'neutrals0' };
  } else if (visited) {
    styleProps = { color: 'neutrals90', background: 'neutrals40' };
  } else {
    styleProps = { color: 'neutrals0', background: 'neutrals100' };
  }

  return (
    <MapMarker
      longitude={longitude}
      latitude={latitude}
      style={focus || hoveredPropertyId === id ? { zIndex: 1 } : { zIndex: 0 }}
    >
      <Flex
        alignItems="center"
        className={cx(ContainerStyles)}
        borderRadius="rounded"
        boxShadow="z1"
        cursor="pointer"
        data-propertyid={id}
        fontWeight="bold"
        justifyContent="center"
        onClick={(e) => {
          // stop propagation of events to fix an issue with clicking map markers
          // ref: https://github.com/visgl/react-map-gl/issues/1847#issuecomment-1111158551
          e.stopPropagation();
          handleOnClick();
        }}
        onMouseOut={handleOnMouseOut}
        onMouseOver={handleOnHover}
        position="relative"
        px={3}
        py={iconConfig?.size && iconConfig.size > 12 ? 1 : 2}
        transform="translateY(-20px)"
        {...styleProps}
      >
        {iconConfig && <Icon color={styleProps.color} {...iconConfig} />}
        {!iconConfig && index !== undefined && (
          <LetterIndex focus={focus || hoveredPropertyId === id}>
            <Text fontSize="s00" fontWeight="semibold" color={styleProps.color}>
              {ALPHABET[index]}
            </Text>
          </LetterIndex>
        )}
        {content && (
          <Text fontSize="s00" fontWeight="semibold" color={styleProps.color}>
            {content}
          </Text>
        )}
      </Flex>
    </MapMarker>
  );
};

type ILetterIndexProps = BoxProps & {
  focus?: boolean;
};

const LetterIndex: React.FC<ILetterIndexProps> = ({ focus, ...props }) => (
  <Flex
    as="span"
    justifyContent="center"
    alignItems="center"
    marginRight="2"
    borderRadius="rounded"
    py="1"
    width={`${space[6]}px`}
    height={`${space[6]}px`}
    backgroundColor={focus ? 'neutrals0' : 'transparent'}
    {...props}
  />
);
